import { createUseStyles } from 'react-jss';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import {
  BackgroundColors,
  TextColors,
  PrimaryColors,
  AbsoluteWhiteColors,
} from '@picsart/design-system/foundation/colors';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { Effects } from '@picsart/design-system/foundation/effects';
import { Shadows } from '@picsart/design-system/foundation/shadows';
import { Easings } from '@picsart/design-system/foundation/easings';
import { Breakpoints } from '@picsart/design-system/foundation/responsive';
import { canHover } from '@picsart/design-system/styleUtils/hoverTouch';
import { BREAKPOINTS } from '@/consts/breakpoints';

const useStyles = createUseStyles(
  {
    root: {
      cursor: 'pointer',
      display: 'block',
      width: '100%',
      // Improve touch target
      minHeight: 44,
      WebkitTapHighlightColor: 'transparent',
      transition: `transform 0.2s ${Easings.easeOut}, box-shadow 0.2s ${Easings.easeOut}`,
      borderRadius: CornerRadius.r12,
      [canHover]: {
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: Shadows.tint2,
        },
      },
      '&:active': {
        transform: 'scale(0.96)',
        boxShadow: Shadows.tint1,
      },
    },
    rootFill: {
      height: '100%',
      minHeight: 0,
    },
    mediaContainer: {
      width: '100%',
      aspectRatio: '1 / 1',
      minHeight: 100, // Minimum height for skeleton to show while loading
      borderRadius: CornerRadius.r12,
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: BackgroundColors.base,
      [`@media (max-width: ${Breakpoints.xs - 1}px)`]: {
        borderRadius: CornerRadius.r8,
      },
      // BREAKPOINTS.narrow (280px) has no DS equivalent — the smallest DS
      // breakpoint is XS (480). This guards ultra-narrow screens where r8
      // looks visually cramped and r12 reads cleaner.
      [`@media (max-width: ${BREAKPOINTS.narrow - 1}px)`]: {
        borderRadius: CornerRadius.r12,
      },
    },
    mediaContainerFill: {
      height: '100%',
      aspectRatio: 'auto',
      minHeight: 0,
    },
    // Selection ring drawn on the media box so it always follows the same
    // (responsive) corner radius as the clipped media — no wrapper can mismatch.
    mediaContainerSelected: {
      boxShadow: `0 0 0 2px ${PrimaryColors.base.default}`,
    },
    // Always-visible save toggle for preset cards (top-right over the media).
    saveButton: {
      position: 'absolute',
      top: Spacings.s8,
      right: Spacings.s8,
      color: TextColors.base,
    },
    // Preset actions, bottom-left over the media. A row rather than a lone
    // absolute button so a second chip can be added without re-deriving the
    // offsets. Only the desktop gallery renders these, so they're hover-only
    // with no touch branch — the title overlay they'd sit on top of fades out
    // on the same hover.
    cornerActions: {
      position: 'absolute',
      bottom: Spacings.s8,
      left: Spacings.s8,
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s8,
      opacity: 0,
      pointerEvents: 'none',
      transition: `opacity 0.2s ${Easings.easeOut}`,
      // One above CardOverlays' top layer (info overlay, 5) so it takes clicks.
      zIndex: 6,
    },
    cornerActionsVisible: {
      opacity: 1,
      pointerEvents: 'auto',
    },
    // Translucent chip rather than a bare icon — the media behind it is
    // arbitrary, and this is the treatment CardActions already uses.
    cornerButton: {
      width: Spacings.s32,
      height: Spacings.s32,
      borderRadius: CornerRadius.circle,
      backgroundColor: Effects.alpha50,
      color: AbsoluteWhiteColors.base.default,
      [canHover]: {
        '&:hover': {
          backgroundColor: Effects.alpha80,
        },
      },
    },
  },
  { name: 'library-content-item' }
);

export default useStyles;
